import { IcLocation } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { useWasteStatus } from "~/pages/dashboard/lib/use-waste-status.hook";

interface PickupLocationProgressProps {
  serviceProviderId?: string | number | bigint;
  districtOrganizationIds?: (string | number | bigint)[];
}

export function PickupLocationProgress({
  serviceProviderId,
  districtOrganizationIds = [],
}: PickupLocationProgressProps) {
  const t = useT();

  const wasteStatus = useWasteStatus({
    districtOrganizationIds,
    serviceProviderIds: serviceProviderId != null ? [serviceProviderId] : [],
  });

  return (
    <div className="space-y-4 py-4">
      {/* Progress bar */}
      <div className="px-4">
        <div className="flex gap-2 items-start w-full">
          <div className="flex items-center shrink-0 w-6 h-6">
            <IcLocation className="w-6 h-6 text-text-primary" />
          </div>
          <div className="flex flex-1 h-6 rounded-lg overflow-hidden">
            {/* Exported */}
            <div
              className="bg-[#34c759] flex items-center justify-center py-1 transition-[flex] duration-500"
              style={{ flex: wasteStatus.locationVisited }}
            >
              {wasteStatus.locationVisited > 0 && (
                <p className="font-medium text-xs leading-4 tracking-tight text-white whitespace-nowrap px-1">
                  {Math.round(wasteStatus.locationVisited)}
                </p>
              )}
            </div>
            {/* Waiting */}
            <div
              className="bg-brand-blue flex items-center justify-center py-1 transition-[flex] duration-500"
              style={{ flex: wasteStatus.locationWaiting }}
            >
              {wasteStatus.locationWaiting > 0 && (
                <p className="font-medium text-xs leading-4 tracking-tight text-white whitespace-nowrap px-1">
                  {Math.round(wasteStatus.locationWaiting)}
                </p>
              )}
            </div>
           
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid gap-y-2 gap-x-12 px-4" style={{ gridTemplateColumns: "1fr auto auto" }}>
        {/* Planned */}
        <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary">
          {t(Namespace.Govdash, "status.planned")}
        </p>
        <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {t(Namespace.Govdash, "status.points_count", { count: wasteStatus.locationPlan })}
        </p>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {wasteStatus.locationPlanPercent}%
        </p>

        {/* Exported */}
        <div className="flex ml-2 gap-1 items-center">
          <div className="w-3 h-3 rounded-full bg-[#34c759] shrink-0" />
          <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary">
            {t(Namespace.Govdash, "status.exported")}
          </p>
        </div>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {t(Namespace.Govdash, "status.points_count", { count: wasteStatus.locationVisited })}
        </p>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {wasteStatus.locationVisitedPercent}%
        </p>

        {/* Waiting */}
        <div className="flex ml-2 gap-1 items-center">
          <div className="w-3 h-3 rounded-full bg-brand-blue shrink-0" />
          <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary">
            {t(Namespace.Govdash, "status.waiting")}
          </p>
        </div>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {t(Namespace.Govdash, "status.points_count", { count: wasteStatus.locationWaiting })}
        </p>
        <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
          {wasteStatus.locationWaitingPercent}%
        </p>

      </div>
    </div>
  );
}
